import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { history } from '../..';
import { IExample } from '../models/example';
import { IAccountInfo, IAccountLoginValues, IAccountRegisterValues } from '../models/account';
import { store } from '../stores/store';
import { IUser, IUserDetail, IUserPhoto } from '../models/user';
import { DataGridResult } from '../models/materialUI/dataGrid';
import { Roles } from '../config/enum';
import { AssetEquipment } from '../models/asset';
import { IEquipment, IStation, ISystem } from '../models/asset';
import { IRBDDiagramRequest, IRBDRequest } from '../models/rbd';
import { IRBDTabelRequest } from '../models/rbd';
import { IRAMStationRequest, IRAMSystemRequest } from '../models/ram';
import { GetPirChartParams, IHomeTableEquipmentChartResponse, IHomeTableEquipmentTable } from '../stores/homeTableEquipmentStore';
import { IAutomationRecommendation } from '../stores/homeAutoRecommendStore';
import { IGenerateDashboardParams } from '../stores/homeTrendChartStore';
import { DashboardTrendChartResponse } from '../models/dashboard';

const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay)
    })
}

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

axios.interceptors.request.use(config => {
    const token = store.commonStore.token;
    if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

axios.interceptors.response.use(async response => {
    if (process.env.NODE_ENV === 'development') await sleep(1500);
    response = readResponseHeader(response);
    return response;
}, (error: AxiosError<any>) => {
    if (!error) return 
    
    const { data, status, config } = error.response!;
    console.log(error.response!);

    switch (status) {
        case 400:
            if (typeof data === 'string') {
                store.snackbarStore.show('error', data);
            }
            if (config.method === 'get' && data.errors.hasOwnProperty('id')) {
                history.push({
                    pathname: '/not-found',
                    search: store.commonStore.stationIdQuery 
                        ? `?stationId=${store.commonStore.stationIdQuery}` 
                        : ''
                });
            }
            if (data.errors) {
                const modalStateErrors = [];
                for (const key in data.errors) {
                    if (data.errors[key]) {
                        modalStateErrors.push(' ' + data.errors[key]);
                    }
                }
                // throw modalStateErrors.flat();
                store.snackbarStore.show('error', modalStateErrors.join());
            }
            break;
        case 401:
            //store.snackbarStore.show('error', 'Error 401 (Unauthorized)');
            break;
        case 403:
            store.snackbarStore.show('error', 'Error 403 (Forbidden)');
            break;
        case 404:
            history.push({
                pathname: '/not-found',
                search: store.commonStore.stationIdQuery 
                    ? `?stationId=${store.commonStore.stationIdQuery}` 
                    : ''
            });
            break;
        case 500:
            store.commonStore.setServerError(data);
            history.push({
                pathname: '/server-error',
                search: store.commonStore.stationIdQuery 
                        ? `?stationId=${store.commonStore.stationIdQuery}` 
                        : ''
            });
            break;
    }
    return Promise.reject(error);
});

const readResponseHeader = (response: AxiosResponse): AxiosResponse => {
    if (response.headers["rowcount"]) { // data grid result
        response.data = new DataGridResult(response.data, parseInt(response.headers["rowcount"]));
        return response as AxiosResponse<DataGridResult<any>>;
    }
    return response;
}

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const handleBlobResponse = (response: AxiosResponse<Blob>) => {
    // create file link in browser's memory
    const href = URL.createObjectURL(response.data);

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    const filename = response.headers["content-disposition"].split('"')[1];
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
}

const requests = {
    get: <T>(url: string, body?: AxiosRequestConfig<any>) => axios.get<T>(url, body).then(responseBody),
    getFile: (url: string, body?: AxiosRequestConfig<any>) => axios.get<Blob>(url, {...body, responseType: 'blob'}).then(response => handleBlobResponse(response)),
    post: <T>(url: string, body?: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T>(url: string, body?: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T>(url: string, body?: {}) => axios.delete<T>(url, { data: body }).then(responseBody),
    postFile: <T>(url: string, body?: {}) => axios.post<T>(url, body, { headers: { "Content-Type": "multipart/form-data" } }).then(responseBody),
}

const Account = {
    current: () => requests.get<IAccountInfo>('/account'),
    login: (user: IAccountLoginValues) => requests.post<IAccountInfo>('/account/login', user),
    register: (user: IAccountRegisterValues) => requests.post<IAccountInfo>('/account/register', user)
}

const User = {
    list: (params: URLSearchParams) => requests.get<DataGridResult<IUser[]>>('/user', { params }),
    detail: (username: string) => requests.get<IUserDetail>(`/user/${username}`),
    create: (user: IUser) => requests.post<void>('/user', user),
    edit: (user: IUser) => requests.put<void>(`/user/${user.username}`, user),
    delete: (username: string) => requests.del<void>(`/user/${username}`),
    upload: (data: IUserPhoto) => requests.postFile<void>(`/user/${data.username}/photo`, data.file),
    addRole: (username: string, role: Roles) => requests.post<void>(`/user/${username}/role`, { name: role }),
    removeRole: (username: string, role: Roles) => requests.del<void>(`/user/${username}/role`, { name: role }),
    resetPass: (username: string) => requests.put<void>(`/user/${username}/reset`),
}

const Role = {

}

const Example = {
    list: (params: URLSearchParams) => requests.get<IExample[]>('/example', { params }), // name must exactly "params"
    listGrid: (params: URLSearchParams) => requests.get<DataGridResult<IExample[]>>('/example/grid', { params }), // because it's a field called "params"
    detail: (id: string) => requests.get<IExample>(`/example/${id}`),
    create: (example: IExample) => requests.post<void>('/example', example),
    edit: (example: IExample) => requests.put<void>(`/example/${example.id}`, example),
    delete: (id: string) => requests.del<void>(`/example/${id}`),
}

const Station = {
    equipments: (stationId: string) => requests.get<AssetEquipment[]>(`/stations/${stationId}/equipments`),
}
const Asset = {
    getStations: () => requests.get<IStation[]>('/stations'),
    getSystems: (stationId: number) => requests.get<ISystem[]>('/systems/', { params: { stationId: stationId } }),
    getEquipments: (systemId: number) => requests.get<IEquipment[]>(`/systems/${systemId}/equipments`),
}

const RBD = {
    save: (rbd: IRBDRequest) => requests.post<void>('/rbds', rbd),
    getStationTable: (stationId: number) => requests.get<IRBDTabelRequest[]>(`/rbds/stations/${stationId}/table`),
    getSystemTable: (systemId: number) => requests.get<IRBDTabelRequest[]>(`/rbds/systems/${systemId}/table`),
    getSystemDiagram: (systemId: number) => requests.get<IRBDDiagramRequest>(`/rbds/systems/${systemId}/diagram`),
    getStationDiagram: (systemId: number) => requests.get<IRBDDiagramRequest>(`/rbds/stations/${systemId}/diagram`)
}

const RAM = {
    getStation: (stationId: string) => requests.get<IRAMStationRequest[]>(`/rams/stations/${stationId}`),
    getSystem: (systemId: string) => requests.get<IRAMSystemRequest[]>(`/rams/systems/${systemId}`),
    download: (stationId: number) => requests.getFile(`/rams/export-to-excel/${stationId}`),
}

const Dashboard = {
    generateChart: (params: IGenerateDashboardParams) => requests.get<DashboardTrendChartResponse>('/dashboards/trendchart-ram', { params }),
    generateChartStation: (params: IGenerateDashboardParams) => requests.get<DashboardTrendChartResponse>('/dashboards/trendchart-ram-station', { params }),
    getEquipmentRam: (stationId: string) => requests.get<IHomeTableEquipmentTable[]>(`/dashboards/equipment-ram/${stationId}`),
    getPieChart: (params: GetPirChartParams) => requests.get<IHomeTableEquipmentChartResponse>('/dashboards/equipment-ram/pie-chart', { params }),
    downloadExcelEquipmentRam: (stationId: number) => requests.getFile(`/dashboards/equipment-ram/export-to-excel/${stationId}`),
    getAutomationRecommendation: (stationId: string) => requests.get<IAutomationRecommendation[]>(`/dashboards/automation-recommendation/${stationId}`)
}

const agent = {
    Account,
    User,
    Role,
    Example,
    Station,
    Asset,
    RBD,
    RAM,
    Dashboard,
}

export default agent;